import { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./auth-context";
import type { Language, TranslationKey } from "@/lib/i18n";
import { t as translate } from "@/lib/i18n";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user, updateUser } = useAuth();
  const [language, setLanguageState] = useState<Language>("en");

  // Load language from user or localStorage
  useEffect(() => {
    if (user?.language) {
      setLanguageState(user.language as Language);
      localStorage.setItem("language", user.language);
    } else {
      const stored = localStorage.getItem("language") as Language;
      if (stored && (stored === "en" || stored === "id")) {
        setLanguageState(stored);
      }
    }
  }, [user]);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
    
    // Update user language in database if user is logged in
    if (user) {
      try {
        const { api } = await import("@/lib/api");
        const data = await api.put<{ user: typeof user }>("/users/me", {
          language: lang,
        });
        updateUser(data.user);
      } catch (error) {
        console.error("Failed to update language:", error);
      }
    }
  };

  const t = (key: TranslationKey): string => {
    return translate(key, language);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

